/**
 * Dev mode: in-browser API mock.
 *
 * Companion to devAuth.ts. Patches `window.fetch` so every call to the app's
 * API base URL is answered locally instead of hitting a backend. On `main` this
 * is deliberately empty: reads return an empty paginated list, writes return an
 * empty object, so the whole app renders its empty states without a single
 * failed request.
 *
 * A prototype branch adds its own data on top WITHOUT editing this file:
 *
 *   // src/prototypes/<topic>/mock.ts
 *   registerDevMockRoutes((request, url) => {
 *     if (request.method === "GET" && url.pathname.endsWith("/contracts/")) {
 *       return jsonResponse(paginated(MY_CONTRACTS));
 *     }
 *     return null; // fall through to the next handler / the empty default
 *   });
 *
 * Handlers run in reverse registration order (last registered wins) and the
 * first one to return a Response answers the call.
 */
import { API_BASE_URL } from "@/lib/api/core";

/** Return a Response to answer the call, or null to fall through to the next handler. */
export type DevMockHandler = (
  request: { method: string; url: string; body?: BodyInit | null },
  url: URL,
) => Response | null | Promise<Response | null>;

const handlers: DevMockHandler[] = [];

/** Register extra mock routes (prototype fixtures). Call before initDevMode(). */
export function registerDevMockRoutes(handler: DevMockHandler): void {
  handlers.push(handler);
}

export function jsonResponse(data: unknown, status = 200): Response {
  if (status === 204) return new Response(null, { status });
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

/** Wrap an array as a DRF-style paginated response ({ results, count, next, previous }). */
export function paginated(results: unknown[]): object {
  return { results, count: results.length, next: null, previous: null };
}

/** Answer anything no handler claimed: empty list for reads, empty object for writes. */
function emptyResponse(method: string): Response {
  if (method === "GET") return jsonResponse(paginated([]));
  if (method === "DELETE") return jsonResponse(null, 204);
  return jsonResponse({});
}

function apiOrigin(): string {
  return new URL(API_BASE_URL, window.location.origin).origin;
}

function apiBasePath(): string {
  return new URL(API_BASE_URL, window.location.origin).pathname.replace(/\/$/, "");
}

let installed = false;

/** Patch window.fetch to serve the app's API locally. Idempotent. */
export function installDevApiMock(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const origin = apiOrigin();
  const basePath = apiBasePath();
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const rawUrl =
      typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = (
      init?.method ?? (typeof input === "object" && "method" in input ? input.method : "GET")
    ).toUpperCase();

    let url: URL;
    try {
      url = new URL(rawUrl, window.location.origin);
    } catch {
      return originalFetch(input, init);
    }

    // Anything that isn't an app API call (Vite HMR, fonts, Sentry, PostHog) passes through.
    if (url.origin !== origin || !url.pathname.startsWith(basePath)) {
      return originalFetch(input, init);
    }

    const request = { method, url: url.href, body: init?.body ?? null };
    for (let i = handlers.length - 1; i >= 0; i--) {
      const response = await handlers[i](request, url);
      if (response) return response;
    }
    return emptyResponse(method);
  };

  console.warn(`[dev-mode] API mock installed for ${origin}${basePath} — no backend is contacted.`);
}
